'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils/tw-merge';
import { UiIcon } from '@/components/ui/UiIcon';
import UiLogo from '@/components/ui/UiLogo';
import { useScrollSpy, useElementInView } from '@/hooks';
import { useDictionary, useLocale, useSiteSettings } from '@/components/providers';
import { HERO_PRIMARY_CTA_ID } from '@/features/hero/hero.constants';
import { NavLink } from '@/features/navigation/NavLink';
import { CtaLink } from '@/features/navigation/CtaLink';
import { getAnchorIds, isNavItemActive } from '@/features/navigation/navigation.utils';
import { ThemeToggle } from '@/features/theme';

// Only phones ever open this panel, and it carries the `@headlessui/react`
// dialog (51 KB raw / 17 KiB gzip), so it loads on demand. `ssr: false` inside
// this `'use client'` module is what actually moves the bytes — a runtime
// condition would not (see AGENTS.md → Performance).
//
// Note: the dialog chunk is still fetched eagerly on pages with a form, because
// the form fields import `Input`/`Select`/`Textarea` from the same package.
// Moving those to native elements would drop it from the eager set entirely.
const NavigationOffCanvas = dynamic(
    () => import('@/features/navigation/NavigationOffCanvas').then((m) => m.NavigationOffCanvas),
    { ssr: false },
);

interface HeaderProps {
    className?: string;
}

const ACTION_LINK_CLASS =
    'rounded-lg px-3.5 py-2 text-sm font-medium text-copy-2 transition-colors hover:bg-surface-2 hover:text-title';

function Header({ className }: HeaderProps) {
    const settings = useSiteSettings();
    const locale = useLocale();
    const { a11y } = useDictionary();
    const pathname = usePathname();
    const [menuOpen, setMenuOpen] = useState(false);
    // Mounting the lazy panel is what triggers its request, so it is gated
    // separately from `menuOpen`: pointer/focus on the trigger loads the chunk,
    // the click then only flips the panel open.
    const [menuMounted, setMenuMounted] = useState(false);

    const mainItems = settings.navigation?.main ?? [];
    const login = settings.navigation?.login ?? null;
    const cta = settings.navigation?.cta ?? null;

    const activeAnchorId = useScrollSpy({ ids: getAnchorIds(mainItems, locale) });

    // Reveal the header CTA only once the hero's primary button has scrolled
    // behind the header (h-16 = 64px). Absent hero (sub-pages) → always shown.
    const heroCtaInView = useElementInView(`#${HERO_PRIMARY_CTA_ID}`, '-64px 0px 0px 0px');
    const showCta = Boolean(cta?.label) && cta?.enabled !== false && !heroCtaInView;

    // Reveal the bottom border only once the first H1 has scrolled behind the
    // header (h-16 = 64px). Absent H1 → border always shown.
    const h1InView = useElementInView('h1', '-64px 0px 0px 0px');
    const showBorder = !h1InView;

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 border-b bg-(--pg-header) backdrop-blur-md transition-[border-color]',
                    showBorder ? 'border-hairline' : 'border-transparent',
                    className,
                )}
            >
                <div className="relative mx-auto flex h-16 max-w-410 items-center px-[var(--space-gutter)]">
                    <button
                        type="button"
                        onClick={() => {
                            setMenuMounted(true);
                            setMenuOpen(true);
                        }}
                        onPointerEnter={() => setMenuMounted(true)}
                        onFocus={() => setMenuMounted(true)}
                        aria-label={a11y.openMenu}
                        aria-haspopup="dialog"
                        aria-expanded={menuOpen}
                        className="-ml-2.25 mr-2 inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-copy-2 transition-colors hover:bg-surface-3 hover:text-title xl:hidden"
                    >
                        <UiIcon name="menu" size={18} />
                    </button>

                    <Link href={`/${locale}`} aria-label={a11y.home} className="z-10 flex shrink-0 items-center">
                        {/* Decorative: the wrapping link already carries the accessible name. */}
                        <UiLogo variant="emblem" className="h-7.5 w-18.25 text-logo xl:h-9 xl:w-22" />
                    </Link>

                    {mainItems.length ? (
                        <nav
                            aria-label={a11y.mainNavigation}
                            className="absolute left-1/2 hidden -translate-x-1/2 xl:block"
                        >
                            <ul className="flex items-center gap-1">
                                {mainItems.map((item) => {
                                    const isActive = isNavItemActive(item, locale, pathname, activeAnchorId);
                                    return (
                                        <li key={item._key}>
                                            <NavLink
                                                item={item}
                                                isActive={isActive}
                                                className={cn(
                                                    'rounded-lg px-3.5 py-2 text-sm font-medium transition-colors',
                                                    isActive ? 'text-title' : 'text-copy-2 hover:bg-surface-2',
                                                )}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>
                    ) : null}

                    <div className="z-10 ml-auto flex items-center gap-2">
                        {login?.enabled !== false && login?.label ? (
                            <NavLink item={login} className={cn('hidden xl:inline-flex', ACTION_LINK_CLASS)} />
                        ) : null}

                        {showCta ? (
                            <CtaLink
                                item={cta}
                                className={cn(
                                    'btn btn-primary btn-sm lg:btn-md inline-flex',
                                    'opacity-100 starting:opacity-0',
                                    'hover:transform-none',
                                )}
                            />
                        ) : null}

                        <ThemeToggle className="-mr-2.5 hidden sm:inline-flex" />
                    </div>
                </div>
            </header>

            {menuMounted ? <NavigationOffCanvas open={menuOpen} onClose={() => setMenuOpen(false)} /> : null}
        </>
    );
}

export default Header;
